import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionButton } from "@jhool-io/fe-components";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { useNotesListFromLocalStorage } from "../../hooks/useNotesListFromLocalStorage";

interface NoteNavigationProps {
    currentNoteId: string;
    basePath: "notes" | "billing-and-claims";
    viewFrom?: string;
    hideNavigation?: boolean;
}

export default function NoteNavigation({
    currentNoteId,
    basePath,
    viewFrom,
    hideNavigation = false,
}: NoteNavigationProps) {
    const { notesList } = useNotesListFromLocalStorage();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const clientId = params.clientId as string;

    const arrayOfNotesToNavigateWith =
        notesList?.data?.data?.map((note) => ({
            note_id: note.note_id,
            client_id: note.client?.client_id || clientId,
        })) || [];

    const getCurrentNoteIndex = () => {
        const getCurrent = arrayOfNotesToNavigateWith.findIndex(
            (note) => note.note_id === currentNoteId
        );
        return getCurrent;
    };

    const handlePrevButtonClick = () => {
        const currentIndex = getCurrentNoteIndex();
        if (currentIndex === -1 || currentIndex === 0) return;

        const prevNote = arrayOfNotesToNavigateWith[currentIndex - 1];
        const queryParam = viewFrom ? `?viewFrom=${viewFrom}` : "";

        navigate(
            `/${basePath}/${prevNote.client_id}/${prevNote.note_id}${queryParam}`,
            {
                state: location.state || null,
            }
        );
    };

    const handleNextButtonClick = () => {
        const currentIndex = getCurrentNoteIndex();
        if (
            currentIndex === -1 ||
            currentIndex === arrayOfNotesToNavigateWith.length - 1
        )
            return;

        const nextNote = arrayOfNotesToNavigateWith[currentIndex + 1];
        const queryParam = viewFrom ? `?viewFrom=${viewFrom}` : "";

        navigate(
            `/${basePath}/${nextNote.client_id}/${nextNote.note_id}${queryParam}`,
            {
                state: location.state || null,
            }
        );
    };

    if (hideNavigation) {
        return null;
    }

    return (
        <div className="flex items-center justify-end gap-x-[--spacing-items] text-base font-medium">
            {notesList.isLoading ? (
                <>
                    <Loader loaderType="inline" />
                    <Loader loaderType="inline" />
                </>
            ) : null}
            {notesList.isSuccess ? (
                <>
                    <ActionButton
                        actionTitle="Previous note"
                        variant="normal"
                        disabled={
                            getCurrentNoteIndex() === -1 ||
                            getCurrentNoteIndex() === 0
                        }
                        onClick={handlePrevButtonClick}
                        className="hover:opacity-70 [transition:opacity_150ms_ease] underline"
                    >
                        <ChevronLeftIcon /> Prev
                    </ActionButton>

                    {arrayOfNotesToNavigateWith.length > 0 &&
                    getCurrentNoteIndex() !== -1 ? (
                        <span className="flex items-center gap-x-1">
                            <span>{getCurrentNoteIndex() + 1}</span>
                            <span className="text-textSec font-normal">OF</span>
                            <span>{arrayOfNotesToNavigateWith.length}</span>
                        </span>
                    ) : null}

                    <ActionButton
                        variant="normal"
                        size="auto"
                        actionTitle="Next note"
                        disabled={
                            getCurrentNoteIndex() === -1 ||
                            getCurrentNoteIndex() ===
                                arrayOfNotesToNavigateWith.length - 1
                        }
                        onClick={handleNextButtonClick}
                        className="hover:opacity-70 [transition:opacity_150ms_ease] underline"
                    >
                        Next
                        <ChevronRightIcon />
                    </ActionButton>
                </>
            ) : null}
        </div>
    );
}
